import { assign, setup } from 'xstate';
import type { ApolloClient } from '@apollo/client';
import type { QuickSearchResult } from '@frameio/graphql-client/generated';
import type { GetQuickSearchResultsV2QueryVariables } from '../gql/generated/GetQuickSearchResultsV2';
import type { QuickSearchBaseContext } from './types';
import {
  fetchLexicalQueryActor,
  fetchNlpQueryActor,
  sharedGuards,
} from './utils';

export type QuickSearchTabbedContext = QuickSearchBaseContext & {
  nlpNodes: QuickSearchResult[];
  nlpError?: Error;
  nlpComplete: boolean;
  scopedToProjectId: string | null;
};

export type QuickSearchTabbedEvents =
  | {
      type: 'SEARCH';
      requestVariables: GetQuickSearchResultsV2QueryVariables;
      apolloClient?: ApolloClient<unknown>;
      scopedToProjectId?: string | null;
    }
  | {
      type: 'RESET';
    };

export type QuickSearchTabbedMachineInput = {
  apolloClient: ApolloClient<unknown>;
  requestVariables: QuickSearchBaseContext['requestVariables'];
  initialLexicalNodes?: QuickSearchResult[];
  initialNlpNodes?: QuickSearchResult[];
  scopedToProjectId: string | null;
};

const tabbedGuards = {
  ...sharedGuards,
  hasInitialNodes: ({ context }: { context: QuickSearchTabbedContext }) =>
    (context.lexicalComplete && context.lexicalNodes.length > 0) ||
    (context.nlpComplete && context.nlpNodes.length > 0),
};

/**
 * Quick Search State Machine - Tabbed Mode
 *
 * Always runs both lexical and NLP queries in parallel.
 */
export const quickSearchTabbedMachine = setup({
  types: {
    context: {} as QuickSearchTabbedContext,
    events: {} as QuickSearchTabbedEvents,
    input: {} as QuickSearchTabbedMachineInput,
  },
  // ==================== GUARDS ====================
  guards: tabbedGuards,
  // ==================== ACTIONS ====================
  actions: {
    updateSearchParams: assign({
      requestVariables: ({ event }) => {
        if (event.type === 'SEARCH') {
          return event.requestVariables;
        }
        return undefined as never;
      },
      apolloClient: ({ event, context }) => {
        if (event.type === 'SEARCH' && event.apolloClient) {
          return event.apolloClient;
        }
        return context.apolloClient;
      },
      scopedToProjectId: ({ event }) => {
        if (event.type === 'SEARCH') {
          return event.scopedToProjectId ?? null;
        }
        return undefined as never;
      },
      lexicalNodes: ({ event }) => {
        if (event.type === 'SEARCH') {
          return [];
        }
        return undefined as never;
      },
      nlpNodes: ({ event }) => {
        if (event.type === 'SEARCH') {
          return [];
        }
        return undefined as never;
      },
      lexicalComplete: () => false,
      nlpComplete: () => false,
    }),
    clearResults: assign({
      lexicalNodes: () => [],
      nlpNodes: () => [],
      lexicalError: () => undefined,
      nlpError: () => undefined,
      lexicalComplete: () => false,
      nlpComplete: () => false,
    }),
    storeLexicalSuccess: assign({
      lexicalNodes: ({ event }) => {
        if ('output' in event) {
          return event.output as QuickSearchResult[];
        }
        return [];
      },
      lexicalError: () => undefined,
      lexicalComplete: () => true,
    }),
    storeLexicalError: assign({
      lexicalNodes: () => [],
      lexicalError: ({ event }) => {
        if ('error' in event) {
          return event.error as Error;
        }
        return undefined;
      },
      lexicalComplete: () => true,
    }),
    storeNlpSuccess: assign({
      nlpNodes: ({ event }) => {
        if ('output' in event) {
          return event.output as QuickSearchResult[];
        }
        return [];
      },
      nlpError: () => undefined,
      nlpComplete: () => true,
    }),
    storeNlpError: assign({
      nlpNodes: () => [],
      nlpError: ({ event }) => {
        if ('error' in event) {
          return event.error as Error;
        }
        return undefined;
      },
      nlpComplete: () => true,
    }),
  },
  // ==================== ACTORS ====================
  actors: {
    fetchLexicalQuery: fetchLexicalQueryActor,
    fetchNlpQuery: fetchNlpQueryActor,
  },
}).createMachine({
  id: 'quickSearchTabbed',
  initial: 'initializing',
  context: ({ input }: { input: QuickSearchTabbedMachineInput }) => ({
    requestVariables: input.requestVariables,
    lexicalNodes: input.initialLexicalNodes ?? [],
    nlpNodes: input.initialNlpNodes ?? [],
    lexicalComplete: input.initialLexicalNodes !== undefined,
    apolloClient: input.apolloClient,
    nlpComplete: input.initialNlpNodes !== undefined,
    scopedToProjectId: input.scopedToProjectId,
  }),
  on: {
    RESET: {
      target: '.idle',
      actions: ['clearResults'],
    },
  },
  // ==================== STATES ====================
  states: {
    initializing: {
      always: [
        {
          guard: 'hasInitialNodes',
          target: 'complete',
        },
        {
          target: 'idle',
        },
      ],
    },
    idle: {
      on: {
        SEARCH: {
          target: 'searching',
          actions: ['updateSearchParams'],
        },
      },
    },
    searching: {
      initial: 'parallelSearching',
      states: {
        parallelSearching: {
          type: 'parallel',
          states: {
            lexicalQuery: {
              initial: 'lexLoading',
              states: {
                lexLoading: {
                  invoke: {
                    id: 'lexicalQueryActor',
                    src: 'fetchLexicalQuery',
                    timeout: 10000,
                    input: ({ context }) => ({
                      apolloClient:
                        context.apolloClient as ApolloClient<unknown>,
                      requestVariables: context.requestVariables,
                    }),
                    onDone: {
                      target: 'lexSuccess',
                      actions: ['storeLexicalSuccess'],
                    },
                    onError: {
                      target: 'lexError',
                      actions: ['storeLexicalError'],
                    },
                  },
                },
                lexSuccess: {
                  type: 'final',
                },
                lexError: {
                  type: 'final',
                },
              },
            },
            nlpQuery: {
              initial: 'nlpLoading',
              states: {
                nlpLoading: {
                  invoke: {
                    id: 'nlpQueryActor',
                    src: 'fetchNlpQuery',
                    input: ({ context }) => ({
                      apolloClient:
                        context.apolloClient as ApolloClient<unknown>,
                      requestVariables: context.requestVariables,
                      scopedToProjectId: context.scopedToProjectId,
                    }),
                    onDone: {
                      target: 'nlpSuccess',
                      actions: ['storeNlpSuccess'],
                    },
                    onError: {
                      target: 'nlpError',
                      actions: ['storeNlpError'],
                    },
                  },
                },
                nlpSuccess: {
                  type: 'final',
                },
                nlpError: {
                  type: 'final',
                },
              },
            },
          },
          onDone: {
            target: '#quickSearchTabbed.complete',
          },
        },
      },
      on: {
        SEARCH: {
          target: '.parallelSearching',
          actions: ['updateSearchParams'],
        },
      },
    },
    complete: {
      on: {
        SEARCH: {
          target: 'searching',
          actions: ['updateSearchParams'],
        },
      },
    },
  },
});
